import { useRef, useEffect, useContext } from "react";
import * as Sentry from "@sentry/react";
import { useAppSelector, useAppDispatch } from "store/store";
import { ClientErrorTypes, ScreenShareStatus } from "globals/enums";
import { canvasDimensions , orgIdsWithRescaleImage } from "config/constant";
import { getScreenGrabBlobPayload } from "helpers/blob-payload";
import { enqueueItem } from "store/screenGrabSlice";
import { MediaContext } from "context/MediaContext";
import { handleError } from "helpers/sentry";
import { parseOrgIds } from "helpers/common";

const useScreenGrab = () => {
  const dispatch = useAppDispatch();
  const storage = useAppSelector((state) => state.app.storage);
  const session = useAppSelector((state) => state.app.session);
  const { screenShareStatus } = useAppSelector((state) => state.screenShare);
  const screenGrab = useAppSelector((state) => state.app.configuration.screen_grab);
  const organizationId = useAppSelector((state) => state.app.organizationId);
  const { displayStream } = useContext(MediaContext);
  const intervalId = useRef<NodeJS.Timer>();
  const storageRef = useRef(storage);

  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  const createVideoElementForRecordingScreenGrabs = (stream: MediaStream) => {
    const screenVideoElement = document.createElement("video");
    screenVideoElement.autoplay = true;
    screenVideoElement.srcObject = stream;

    screenVideoElement.addEventListener("loadedmetadata", () => {
      Sentry.addBreadcrumb({
        category: "screen-grab",
        message: "Video element loadedmetadata",
        level: "info",
        data: {
          videoWidth: screenVideoElement.videoWidth,
          videoHeight: screenVideoElement.videoHeight,
          readyState: screenVideoElement.readyState,
        },
      });
    }, { once: true });

    screenVideoElement.addEventListener("playing", () => {
      Sentry.addBreadcrumb({
        category: "screen-grab",
        message: "Video element playing",
        level: "info",
        data: { currentTime: screenVideoElement.currentTime },
      });
    }, { once: true });

    screenVideoElement.addEventListener("stalled", () => {
      Sentry.addBreadcrumb({
        category: "screen-grab",
        message: "Video element stalled",
        level: "warning",
        data: {
          currentTime: screenVideoElement.currentTime,
          readyState: screenVideoElement.readyState,
        },
      });
    });

    return screenVideoElement;
  };

  const createCanvasForScreenShareVideoStream = (screenVideoElement: HTMLVideoElement) => {
    const { canvasWidth, canvasHeight } = canvasDimensions;
    const screenGrabCanvas = document.createElement("canvas");
    const canvasContext = screenGrabCanvas.getContext("2d");
    screenVideoElement.autoplay = true;
    screenGrabCanvas.width = canvasWidth;
    screenGrabCanvas.height = canvasHeight;
    
    // Calculate dimensions to maintain aspect ratio
    const videoWidth = screenVideoElement.videoWidth;
    const videoHeight = screenVideoElement.videoHeight;
    let drawWidth = canvasWidth;
    let drawHeight = canvasHeight;
    let offsetX = 0;
    let offsetY = 0;
    
    // Calculate the scaling factor to fit the video in the canvas while preserving aspect ratio
    const videoRatio = videoWidth / videoHeight;
    const canvasRatio = canvasWidth / canvasHeight;
    
    if (videoRatio > canvasRatio) {
      // Video is wider than canvas (relative to height)
      drawHeight = canvasWidth / videoRatio;
      offsetY = (canvasHeight - drawHeight) / 2;
    } else {
      // Video is taller than canvas (relative to width)
      drawWidth = canvasHeight * videoRatio;
      offsetX = (canvasWidth - drawWidth) / 2;
    }
    
    // Fill with black background
    if (canvasContext) {
      canvasContext.fillStyle = "#000000";
      canvasContext.fillRect(0, 0, canvasWidth, canvasHeight);

      // Draw the video with proper aspect ratio
      const shouldRescale = parseOrgIds(orgIdsWithRescaleImage).includes(organizationId);
      if (shouldRescale) {
        // Apply rescaling logic
        canvasContext.drawImage(screenVideoElement, offsetX, offsetY, drawWidth, drawHeight);
      } else {
        // Draw without rescaling
        canvasContext.drawImage(screenVideoElement, 0, 0, canvasWidth, canvasHeight);
      }
    }
    return screenGrabCanvas;
  };

  const startScreenCapture = (stream: MediaStream) => {
    const screenVideoElement = createVideoElementForRecordingScreenGrabs(stream);
    intervalId.current = setInterval(async () => {
      try {
        const videoWidth = screenVideoElement.videoWidth;
        const videoHeight = screenVideoElement.videoHeight;
        const readyState = screenVideoElement.readyState;
        const currentTime = screenVideoElement.currentTime;

        Sentry.addBreadcrumb({
          category: "screen-grab",
          message: "Capturing screen grab",
          level: "info",
          data: { videoWidth, videoHeight, readyState, currentTime },
        });

        if (videoWidth === 0 || videoHeight === 0) {
          Sentry.captureMessage("Screen grab: video element has zero dimensions at capture time", {
            level: "warning",
            extra: { videoWidth, videoHeight, readyState, currentTime },
          });
        }

        const screenGrabCanvas = createCanvasForScreenShareVideoStream(screenVideoElement);
        const blob = await new Promise<Blob | null>((resolve) => {
          screenGrabCanvas.toBlob(resolve, "image/png");
        });

        if (!blob) {
          Sentry.captureMessage("Screen grab: canvas.toBlob() returned null", {
            level: "warning",
            extra: { videoWidth, videoHeight, readyState, currentTime },
          });
          return;
        }

        Sentry.addBreadcrumb({
          category: "screen-grab",
          message: "Screen grab blob created",
          level: "info",
          data: { blobSize: blob.size, videoWidth, videoHeight },
        });

        dispatch(enqueueItem(getScreenGrabBlobPayload(storageRef.current, session, blob)));
      } catch (error) {
        console.log("Error capturing screen grab:", error);
        handleError(error, ClientErrorTypes.ScreenGrabError);
      }
    }, 10000);
  };

  const stopScreenShare = () => {
    clearInterval(intervalId.current as any);
  };

  useEffect(() => {
    if (!screenGrab) {
      return;
    }
    if (screenShareStatus === ScreenShareStatus.ScreenShareStopped) {
      stopScreenShare();
    }
    if (screenShareStatus === ScreenShareStatus.ScreenShareStarted && displayStream) {
      startScreenCapture(displayStream);
    }
    return () => {
      stopScreenShare();
    };
  }, [screenShareStatus, displayStream]);
};

export default useScreenGrab;
